<template>
  <div :class="wrapperClass">
    <div v-if="label" class="logo-card__title">{{ label }}</div>

    <div v-if="description" class="p-mt-2 p-mb-2 p-text-secondary">
      <small>{{ description }}</small>
    </div>

    <div class="logo-card__preview"
         :style="previewBoxStyle"
         :class="[
           previewClass,
           { 'is-drag-over': dragOver },
           { 'no-pointer': disabled }
         ]"
         @dragenter.prevent="onDragEnter"
         @dragover.prevent="onDragOver"
         @dragleave.prevent="onDragLeave"
         @drop.prevent="onDrop"
         @click="onZoneClick">
      <!-- SINGLE preview (new file) -->
      <img v-if="!multiple && previewUrl"
           :src="previewUrl"
           class="logo-img"
           :style="singleImgStyle"/>

      <!-- SINGLE preview (existing file) -->
      <img v-else-if="!multiple && effectiveCurrentUrl && currentUrlOk"
           :src="effectiveCurrentUrl"
           class="logo-img"
           :style="singleImgStyle"
           @load="onCurrentImgLoad"
           @error="onCurrentImgError"/>

      <!-- MULTIPLE preview grid -->
      <div v-else-if="multiple && previews.length"
           class="multi-preview-grid">
        <div v-for="(p, i) in previews"
             :key="i"
             class="multi-preview-item"
             :style="{ width: itemWidth + 'px', height: itemHeight + 'px' }">
          <template v-if="p.isImage && p.url">
            <img :src="p.url" class="multi-preview-img" />
          </template>
          <template v-else>
            <div class="multi-preview-placeholder">
              <i class="multi-preview-icon pi" :class="fileIconClass(p.file)"/>
              <span class="multi-preview-filename">
                {{ p.file?.name }}
              </span>
            </div>
          </template>

          <Button
              icon="pi pi-times"
              class="p-button-rounded p-button-text multi-remove"
              :class="buttonSizeClass"
              @click.stop="removeAt(i)"
              :disabled="disabled || isUploading"
              aria-label="Remove"/>
        </div>
      </div>

<!--      <div v-else class="logo-placeholder">-->
<!--        {{ dropPlaceholderText }}-->
<!--      </div>-->

<!--      <div v-if="dragOver" class="drag-hint">-->
<!--        {{ $t('fileDropToSelectLabel') }}-->
<!--      </div>-->

      <div v-else class="logo-placeholder">
<!--        {{ dragOver ? $t('fileDropToSelectLabel') : dropPlaceholderText }}-->
        {{ dropPlaceholderText }}
      </div>

      <!-- Single delete button bottom-right -->
      <Button
          v-if="showSingleDeleteBtn"
          icon="pi pi-trash"
          class="single-delete-btn p-button-rounded p-button-danger"
          :class="buttonSizeClass"
          @click.stop="deleteOne"
          :disabled="disabled || isUploading || isDeleting"
          :loading="isDeleting"
          aria-label="Delete"/>
    </div>

    <!-- meta -->
    <div class="logo-card__meta">
      <template v-if="!multiple">
        <span class="file-name p-text-secondary" v-if="file">{{ file.name }}</span>
        <span class="file-name p-text-secondary" v-else>{{ $t('chooseFileLabel') }}</span>
      </template>

      <template v-else>
        <span class="file-name p-text-secondary" v-if="files.length">
          {{ $t('fileSelectedCountLabel', { count: files.length }) }}
        </span>
        <span class="file-name p-text-secondary" v-else>
          {{ $t('chooseFilesLabel') }}
        </span>
      </template>

      <span v-if="showRemainingFiles && multiple && maxFiles && remainingFiles !== null" class="file-limit-hint p-text-secondary">
          <template v-if="$t">
            {{ $t('filesUploadRemaining', { count: remainingFiles }) }}
          </template>
          <template v-else>
            You can upload {{ remainingFiles }} more file(s)
          </template>
          <span v-if="maxSizeMb">. {{ $t('maxFileSize') }}: {{ maxSizeMb }} {{ $t('MB') }}</span>
      </span>

    </div>

    <div class="logo-card__actions">
      <input
          ref="input"
          type="file"
          :accept="acceptAttr"
          class="hidden-input"
          :multiple="multiple"
          @change="onFilePicked"/>

      <Button icon="pi pi-folder-open"
              :label="$t('chooseFileLabel')"
              style="width: auto;"
              v-show="showButton"
              class="p-button-outlined"
              :class="buttonSizeClass"
              @click="onChooseClick"
              :disabled="disabled || isUploading"/>

      <Button v-if="!multiple"
              icon="pi pi-upload"
              :label="$t('uploadFileLabel')"
              style="width: auto;"
              v-show="showButton"
              class="p-button-raised"
              :class="buttonSizeClass"
              :disabled="uploadDisabled|| disabled"
              :loading="isUploading"
              @click="upload"/>

      <!-- Reset selected (single / multi) -->
      <Button icon="pi pi-times"
              :label="$t('Clear')"
              style="width: auto;"
              class="p-button-text"
              :class="buttonSizeClass"
              v-show="showButton"
              v-if="(!multiple && file) || (multiple && files.length)"
              @click="onResetClick"
              :disabled="disabled || isUploading"
              aria-label="Reset"/>
    </div>

    <!-- ===== Simple built-in Cropper Modal ===== -->
    <Modal v-model:visible="cropperVisible"
           modal
           :header="$t('cropAvatarTitle')"
           :style="{ width: cropperDialogWidth }"
           :dismissableMask="true"
           @close="onCropCancel"
           @hide="onCropCancel">
      <div class="cropper-modal">
        <div
            class="cropper-viewport"
            :style="{ width: cropViewportPx + 'px', height: cropViewportPx + 'px' }"
            @pointerdown="onCropPointerDown"
            @pointermove="onCropPointerMove"
            @pointerup="onCropPointerUp"
            @pointercancel="onCropPointerUp"
            @pointerleave="onCropPointerUp">
          <img
              ref="cropImg"
              :src="cropperSrc"
              class="cropper-img"
              :style="cropImgStyle"
              draggable="false"
              @load="onCropImgLoad"/>
          <div class="cropper-mask"></div>
        </div>

        <div class="cropper-controls">
          <div class="zoom-row">
            <span class="zoom-label">{{ $t('zoomLabel') }}</span>
            <input
                type="range"
                min="1"
                max="3"
                step="0.01"
                v-model.number="cropZoom"
                @input="recalcCropPosition(true)"/>
          </div>
        </div>
      </div>

      <template #footer>
        <Button :label="$t('Cancel')"
                class="p-button-text"
                @click="onCropCancel"/>
        <Button :label="$t('Apply')"
                icon="pi pi-check"
                :disabled="disabled"
                class="p-button-text"
                @click="onCropApply"/>
      </template>
    </Modal>
    <!-- ===== /Cropper Modal ===== -->
  </div>
</template>

<script>
import httpClient from "@/services/http.services"
import showErrorsMixins from "@/mixins/showErrorsMixins"
import settings from "@/settings";

export default {
  name: "FileUploader",
  mixins: [showErrorsMixins],
  emits: ['updated', 'deleted', "select", "clear", "remove"],

  props: {
    previewClass: {
      type: String,
      default: 'file-dropzone',
    },
    maxFiles: { type: Number, default: null },
    maxFilesPerUpload: { type: Number, default: null },
    existingFilesCount: { type: Number, default: 0 },
    item: {
      type: Object,
      default: null,
    },
    label: { type: String, default: '' },
    description: { type: String, default: '' },
    field: { type: String, required: true },
    currentUrl: { type: String, default: null },
    endpoint: { type: String, default: null },

    deleteEndpoint: { type: String, default: null },

    idParam: { type: String, default: "id" },
    extraData: { type: Object, default: () => ({}) },

    multiple: { type: Boolean, default: false },

    showButton: { type: Boolean, default: true },
    showRemainingFiles: { type: Boolean, default: true },

    maxSizeMb: { type: Number, default: null },

    accept: { type: String, default: null },

    imagesOnly: { type: Boolean, default: true },
    allowNonImages: { type: Boolean, default: false },

    disabled: { type: Boolean, default: false },

    wrapperClass: { type: [String, Array, Object], default: "" },

    itemWidth: { type: Number, default: null },
    itemHeight: { type: Number, default: 104 },

    buttonSize: {
      type: String,
      default: 'sm',
      validator: (v) => ['md', 'sm'].includes(v),
    },

    maxWidth: { type: Number, default: 600 },
    maxHeight: { type: Number, default: 150 },
    jpegQuality: { type: Number, default: 0.82 },
    resizeMode: { type: String, default: "contain" }, // contain|cover
    autoResize: { type: Boolean, default: true },

    enableCropper: { type: Boolean, default: false },
    cropToSquare: { type: Boolean, default: false },
    squareSize: { type: Number, default: 256 },
    cropViewportPx: { type: Number, default: 300 },
    cropperDialogWidth: { type: String, default: "520px" },
  },

  data() {
    return {
      currentImgLoaded: false,
      dragOver: false,
      dragCounter: 0,

      file: null,
      previewUrl: null,

      files: [],
      previews: [],
      fileKeys: new Set(),

      currentUrlOk: true,
      localCurrentUrl: null,

      suppressCurrent: false,

      isUploading: false,
      isDeleting: false,

      // cropper
      cropperVisible: false,
      cropperSrc: null,
      cropperOriginalFile: null,
      cropImgNaturalW: 0,
      cropImgNaturalH: 0,
      cropBaseScale: 1,
      cropZoom: 1,
      cropX: 0,
      cropY: 0,
      cropDragging: false,
      cropDragStartX: 0,
      cropDragStartY: 0,
      cropStartX: 0,
      cropStartY: 0,
    }
  },

  created() {
    this.currentImgLoaded = false
    this.suppressCurrent = !this.isValidUrl(this.currentUrl)
  },

  computed: {
    remainingFiles() {
      if (!this.multiple || !this.maxFiles) return null

      const used =
          (this.existingFilesCount || 0) +
          (this.files ? this.files.length : 0)

      return Math.max(this.maxFiles - used, 0)
    },
    singleImgStyle() {
      return {
        maxHeight: this.itemHeight + "px", // <= 100
        height: "auto",
        width: "auto",
        maxWidth: "100%",
        objectFit: "contain",
      }
    },
    buttonSizeClass() {
      return this.buttonSize === "sm" ? "p-button-sm" : "p-button-md"
    },

    uploadDisabled() {
      if (this.showButton && !this.endpoint) return true
      if (this.disabled || this.isUploading) return true
      if (!this.multiple && !this.file) return true
      if (this.multiple && !this.files.length) return true
      return false
    },

    effectiveCurrentUrl() {
      if (this.suppressCurrent) return null
      const u = this.localCurrentUrl || this.currentUrl
      return this.isValidUrl(u) ? u : null
    },

    previewBoxStyle() {
      // SINGLE
      if (!this.multiple) {
        const style = {
          minHeight: this.itemHeight + "px",
        }

        if (typeof this.itemWidth === "number" && !isNaN(this.itemWidth)) {
          style.width = this.itemWidth + "px"
        } else {
          style.width = "100%"
        }

        return style
      }

      return {
        minHeight: this.itemHeight + "px",
        width: "100%",
      }
    },

    cropImgStyle() {
      const scale = this.cropBaseScale * this.cropZoom
      return { transform: `translate(${this.cropX}px, ${this.cropY}px) scale(${scale})` }
    },

    showSingleDeleteBtn() {
      if (this.multiple || this.disabled) return false
      const hasPreview = !!this.previewUrl
      const hasCurrentLoaded =
          !!this.effectiveCurrentUrl && this.currentUrlOk && this.currentImgLoaded
      return hasPreview || hasCurrentLoaded
    },

    acceptAttr() {
      if (this.accept) return this.accept
      return this.imagesOnly ? "image/*" : undefined
    },

    dropPlaceholderText() {
      return this.imagesOnly
          ? this.$t('fileDropImagePlaceholder')
          : this.$t('fileDropFilePlaceholder')
    },
  },

  watch: {
    currentUrl(newVal) {
      this.currentImgLoaded = false
      this.currentUrlOk = true
      this.localCurrentUrl = null
      this.suppressCurrent = !this.isValidUrl(newVal)
    },
    '$store.state.firstLayerIsOpened'(value) {
      if (!value && this.cropperVisible) this.closeCropperModal()
    }
  },

  mounted() {
    this._preventWindowDrop = (e) => {
      e.preventDefault()
      e.stopPropagation()
    }
    window.addEventListener("dragover", this._preventWindowDrop)
    window.addEventListener("drop", this._preventWindowDrop)
  },

  beforeUnmount() {
    window.removeEventListener("dragover", this._preventWindowDrop)
    window.removeEventListener("drop", this._preventWindowDrop)
    this.reset(true)
  },

  methods: {
    onZoneClick() {
      if (this.disabled || this.isUploading) return
      this.$refs.input?.click()
    },
    onChooseClick() {
      if (this.disabled || this.isUploading) return
      this.$refs.input?.click()
    },
    fileIconClass(file) {
      if (!file) return 'pi-file'

      const type = (file.type || '').toLowerCase()
      const name = (file.name || '').toLowerCase()

      if (type.startsWith('image/')) return 'pi-image'

      if (this.isPdfFile(type, name)) return 'pi-file-pdf'
      if (this.isWordFile(type, name)) return 'pi-file-word'
      if (this.isExcelFile(type, name)) return 'pi-file-excel'

      return 'pi-file'
    },

    isPdfFile(type, name) {
      return (
          type === 'application/pdf' ||
          name.endsWith('.pdf')
      )
    },

    isWordFile(type, name) {
      return (
          type === 'application/msword' ||
          type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
          name.endsWith('.doc') ||
          name.endsWith('.docx')
      )
    },

    isExcelFile(type, name) {
      return (
          type === 'application/vnd.ms-excel' ||
          type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
          name.endsWith('.xls') ||
          name.endsWith('.xlsx')
      )
    },
    openCropperModal() {
      this.cropperVisible = true
      this.$store.commit('toggleFirstLayer', true)
    },
    closeCropperModal() {
      this.cropperVisible = false
      this.$store.commit('toggleFirstLayer', false)
    },
    clearAttachments() {
      this.reset(false)
      this.emitClear()
    },

    emitSelect() {
      const files = this.multiple
          ? [...this.files]
          : this.file
              ? [this.file]
              : []
      this.$emit("select", { files })
    },

    emitClear() {
      this.$emit("clear", { files: [] })
    },

    emitRemove() {
      const files = this.multiple ? [...this.files] : this.file ? [this.file] : []
      this.$emit("remove", { files })
    },

    onResetClick() {
      this.reset(false)
      this.emitClear()
    },

    onCurrentImgLoad() {
      this.currentImgLoaded = true
      this.currentUrlOk = true
    },

    onCurrentImgError() {
      this.currentImgLoaded = false
      this.currentUrlOk = false
    },

    isValidUrl(u) {
      if (!u || typeof u !== "string") return false
      const s = u.trim().toLowerCase()
      if (!s) return false
      if (s === "null" || s === "undefined") return false
      return true
    },

    isFileTooLarge(file) {
      if (!this.maxSizeMb) return false
      return file.size > this.maxSizeMb * 1024 * 1024
    },

    fileKey(f) {
      return `${f.name.toLowerCase()}__${f.size}`
    },

    onDragEnter(e) {
      if (this.disabled || this.isUploading) return
      e.preventDefault()
      e.stopPropagation()
      this.dragCounter++
      this.dragOver = true
    },

    onDragOver(e) {
      if (this.disabled || this.isUploading) return
      e.preventDefault()
      e.stopPropagation()
      if (e.dataTransfer) e.dataTransfer.dropEffect = "copy"
      this.dragOver = true
    },

    onDragLeave(e) {
      if (this.disabled || this.isUploading) return
      e.preventDefault()
      e.stopPropagation()
      this.dragCounter--
      if (this.dragCounter <= 0) {
        this.dragOver = false
        this.dragCounter = 0
      }
    },

    async onDrop(e) {
      if (this.disabled || this.isUploading) return
      e.preventDefault()
      e.stopPropagation()
      this.dragOver = false
      this.dragCounter = 0

      const dropped = Array.from(e.dataTransfer?.files || [])
      if (!dropped.length) return
      await this.handleFiles(dropped)
    },

    async onFilePicked(e) {
      if (this.disabled || this.isUploading) {
        e.target.value = ""
        return
      }

      const picked = Array.from(e.target.files || [])
      if (!picked.length) return

      await this.handleFiles(picked)
      e.target.value = ""
    },

    async handleFiles(list) {
      if (this.disabled || this.isUploading) return

      const all = Array.from(list || [])
      if (!all.length) return

      const accepted = []
      let hasRejectedFormat = false
      let hasRejectedSize = false
      let hasRejectedByBatch = false
      let hasRejectedByTotal = false

      for (const f of all) {
        const isImage = !!f.type && f.type.startsWith("image/")

        if (this.imagesOnly && !isImage) {
          hasRejectedFormat = true
          continue
        }

        if (!isImage && !this.allowNonImages) {
          hasRejectedFormat = true
          continue
        }

        accepted.push({ file: f, isImage })
      }

      if (hasRejectedFormat) {
        this.$toast?.add({
          severity: "warn",
          detail: this.$t?.('wrongFileFormatMsg') || 'Some files were rejected by format.',
          life: settings.toastLife,
        })
      }

      if (!accepted.length) return

      let filesToProcess = accepted
      if (this.multiple && this.maxFilesPerUpload && accepted.length > this.maxFilesPerUpload) {
        filesToProcess = accepted.slice(0, this.maxFilesPerUpload)
        hasRejectedByBatch = true
      }

      let maxTotal = this.maxFiles || null
      let currentTotal = this.existingFilesCount + (this.multiple ? this.files.length : (this.file ? 1 : 0))

      if (this.multiple && maxTotal && currentTotal >= maxTotal) {
        this.$toast?.add({
          severity: "warn",
          detail: this.$t?.('maximumFilesReachedMsg') || 'Maximum number of files reached.',
          life: settings.toastLife,
        })
        return
      }

      for (let { file: f, isImage } of filesToProcess) {
        if (!this.multiple && this.enableCropper && isImage) {
          this.openCropper(f)
          return
        }

        if (this.autoResize && isImage) {
          const resized = await this.resizeImage(f)
          if (!resized) {
            this.$toast?.add({
              severity: "warn",
              detail: this.$t?.('cannotReadImageFileMsg') || 'Cannot read this image file.',
              life: settings.toastLife,
            })
            continue
          }
          f = resized
        }

        if (this.isFileTooLarge(f)) {
          hasRejectedSize = true
          continue
        }

        if (this.multiple && maxTotal && currentTotal >= maxTotal) {
          hasRejectedByTotal = true
          break
        }

        if (!this.multiple) {
          this.setSingle(f, isImage)
          this.emitSelect()
          return
        }

        const key = this.fileKey(f)
        if (this.fileKeys.has(key)) {
          this.$toast?.add({
            severity: "info",
            detail: this.$t?.('fileAlreadyAddedMsg') || "File already added",
            life: settings.toastLife,
          })
          continue
        }

        this.fileKeys.add(key)

        const url = isImage ? URL.createObjectURL(f) : null

        this.files.push(f)
        this.previews.push({ file: f, url, isImage })
        currentTotal++
      }

      if (hasRejectedSize && this.maxSizeMb) {
        const detail = this.$t
            ? this.$t('fileTooLarge', { max: this.maxSizeMb })
            : `Some files are too large. Max ${this.maxSizeMb} MB.`;
        this.$toast?.add({
          severity: "warn",
          detail,
          life: settings.toastLife,
        })
      }

      if (hasRejectedByBatch && this.maxFilesPerUpload) {
        const count = this.maxFilesPerUpload

        this.$toast?.add({
          severity: "warn",
          detail: this.$t ? this.$t('filesUploadBatchLimit', { count })
              : `You can upload up to ${count} files at once.`,
          life: settings.toastLife,
        })
      }

      if (hasRejectedByTotal && this.maxFiles) {
        this.$toast?.add({
          severity: "warn",
          detail: this.$t?.('maximumFilesReachedMsg') || 'Maximum number of files reached.',
          life: settings.toastLife,
        })
      }

      if (this.multiple) {
        this.emitSelect()
      }
    },



    setSingle(f, isImage = true) {
      this.reset(false)
      this.file = f
      this.previewUrl = isImage ? URL.createObjectURL(f) : null
      this.localCurrentUrl = null
      this.suppressCurrent = false
      this.currentImgLoaded = false
    },

    removeAt(i) {
      const p = this.previews[i]
      if (!p) return
      const key = this.fileKey(p.file)

      if (p.url) URL.revokeObjectURL(p.url)
      this.previews.splice(i, 1)
      this.files.splice(i, 1)
      this.fileKeys.delete(key)

      this.emitRemove()
    },

    // ===== Cropper =====
    openCropper(file) {
      if (this.cropperSrc) URL.revokeObjectURL(this.cropperSrc)
      this.cropperOriginalFile = file
      this.cropperSrc = URL.createObjectURL(file)
      this.cropZoom = 1
      // this.cropperVisible = true
      this.openCropperModal()
    },

    onCropImgLoad(e) {
      const img = e.target
      this.cropImgNaturalW = img.naturalWidth
      this.cropImgNaturalH = img.naturalHeight
      const V = this.cropViewportPx
      this.cropBaseScale = Math.max(
          V / this.cropImgNaturalW,
          V / this.cropImgNaturalH
      )
      this.recalcCropPosition(true)
    },

    recalcCropPosition(center = false) {
      const V = this.cropViewportPx
      const scale = this.cropBaseScale * this.cropZoom
      const scaledW = this.cropImgNaturalW * scale
      const scaledH = this.cropImgNaturalH * scale

      if (center) {
        this.cropX = (V - scaledW) / 2
        this.cropY = (V - scaledH) / 2
      }

      const minX = V - scaledW
      const minY = V - scaledH
      this.cropX = Math.min(0, Math.max(minX, this.cropX))
      this.cropY = Math.min(0, Math.max(minY, this.cropY))
    },

    onCropPointerDown(e) {
      this.cropDragging = true
      this.cropDragStartX = e.clientX
      this.cropDragStartY = e.clientY
      this.cropStartX = this.cropX
      this.cropStartY = this.cropY
      e.target.setPointerCapture?.(e.pointerId)
    },

    onCropPointerMove(e) {
      if (!this.cropDragging) return
      const dx = e.clientX - this.cropDragStartX
      const dy = e.clientY - this.cropDragStartY
      this.cropX = this.cropStartX + dx
      this.cropY = this.cropStartY + dy
      this.recalcCropPosition(false)
    },

    onCropPointerUp() {
      this.cropDragging = false
    },

    onCropCancel() {
      this.closeCropperModal()
      if (this.cropperSrc) URL.revokeObjectURL(this.cropperSrc)
      this.cropperSrc = null
      this.cropperOriginalFile = null
    },

    async onCropApply() {
      const V = this.cropViewportPx
      const scale = this.cropBaseScale * this.cropZoom

      const srcX = -this.cropX / scale
      const srcY = -this.cropY / scale
      const srcW = V / scale
      const srcH = V / scale

      const outSize = this.cropToSquare
          ? this.squareSize
          : Math.min(this.maxWidth, this.maxHeight)
      const canvas = document.createElement("canvas")
      canvas.width = outSize
      canvas.height = outSize
      const ctx = canvas.getContext("2d")
      const imgEl = this.$refs.cropImg

      ctx.fillStyle = "#fff"
      ctx.fillRect(0, 0, outSize, outSize)
      ctx.drawImage(imgEl, srcX, srcY, srcW, srcH, 0, 0, outSize, outSize)

      const blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, "image/jpeg", this.jpegQuality)
      )
      if (!blob) {
        this.onCropCancel()
        return
      }

      const outName = this.cropperOriginalFile.name.replace(
          /\.\w+$/,
          ""
      ) + ".jpg"
      const outFile = new File([blob], outName, { type: "image/jpeg" })

      this.closeCropperModal()

      if (this.cropperSrc) URL.revokeObjectURL(this.cropperSrc)
      this.cropperSrc = null
      this.cropperOriginalFile = null

      this.setSingle(outFile)
      this.emitSelect()
    },
    // ===== /Cropper =====
    async resizeImage(file) {
      try {
        const dataUrl = await new Promise((resolve, reject) => {
          const r = new FileReader()
          r.onload = () => resolve(r.result)
          r.onerror = reject
          r.readAsDataURL(file)
        })

        const img = await new Promise((resolve, reject) => {
          const i = new Image()
          i.onload = () => resolve(i)
          i.onerror = reject
          i.src = dataUrl
        })

        const srcW = img.width
        const srcH = img.height

        const srcMime = file.type || "image/jpeg"
        const supportsAlpha = srcMime === "image/png" || srcMime === "image/webp"
        const outMime = supportsAlpha ? srcMime : "image/jpeg"
        const outExt =
            supportsAlpha
                ? (srcMime === "image/png" ? ".png" : ".webp")
                : ".jpg"

        const makeFileFromCanvas = async (canvas) => {
          const blob = await new Promise((resolve) =>
              canvas.toBlob(
                  resolve,
                  outMime,
                  supportsAlpha ? undefined : this.jpegQuality
              )
          )
          if (!blob || blob.size >= file.size) return file

          const base = file.name.replace(/\.\w+$/, "")
          const newName = base + outExt
          return new File([blob], newName, { type: outMime })
        }

        if (this.cropToSquare) {
          const dst = this.squareSize
          const canvas = document.createElement("canvas")
          canvas.width = dst
          canvas.height = dst
          const ctx = canvas.getContext("2d")

          if (!supportsAlpha) {
            ctx.fillStyle = "#ffffff"
            ctx.fillRect(0, 0, dst, dst)
          } else {
            ctx.clearRect(0, 0, dst, dst)
          }

          const scale = Math.max(dst / srcW, dst / srcH)
          const drawW = srcW * scale
          const drawH = srcH * scale
          const dx = (dst - drawW) / 2
          const dy = (dst - drawH) / 2
          ctx.drawImage(img, dx, dy, drawW, drawH)

          return await makeFileFromCanvas(canvas)
        }

        if (this.resizeMode === "contain") {
          const maxW = this.maxWidth || srcW
          const maxH = this.maxHeight || srcH

          const scale = Math.min(
              1,
              Math.min(maxW / srcW, maxH / srcH)
          )

          const dstW = Math.round(srcW * scale)
          const dstH = Math.round(srcH * scale)

          const canvas = document.createElement("canvas")
          canvas.width = dstW
          canvas.height = dstH
          const ctx = canvas.getContext("2d")

          if (!supportsAlpha) {
            ctx.fillStyle = "#ffffff"
            ctx.fillRect(0, 0, dstW, dstH)
          } else {
            ctx.clearRect(0, 0, dstW, dstH)
          }

          ctx.drawImage(img, 0, 0, dstW, dstH)

          return await makeFileFromCanvas(canvas)
        }

        // --- 3) cover ---
        const dstW = this.maxWidth
        const dstH = this.maxHeight

        const canvas = document.createElement("canvas")
        canvas.width = dstW
        canvas.height = dstH
        const ctx = canvas.getContext("2d")

        if (!supportsAlpha) {
          ctx.fillStyle = "#ffffff"
          ctx.fillRect(0, 0, dstW, dstH)
        } else {
          ctx.clearRect(0, 0, dstW, dstH)
        }

        const scale = Math.max(dstW / srcW, dstH / srcH)
        const drawW = srcW * scale
        const drawH = srcH * scale
        const dx = (dstW - drawW) / 2
        const dy = (dstH - drawH) / 2
        ctx.drawImage(img, dx, dy, drawW, drawH)

        return await makeFileFromCanvas(canvas)
      } catch (err) {
        return null
      }
    },

    async upload() {
      if (this.disabled || this.isUploading) return false

      const id = this.item?.[this.idParam] || this.item?.id
      if (!id || !this.endpoint) return

      this.isUploading = true
      this.currentUrlOk = true

      try {
        let data = null
        if (!this.multiple) {
          data = await this.uploadOne(id, this.file)
        } else {
          for (const f of this.files) {
            data = await this.uploadOne(id, f)
          }
        }

        this.applyServerUrl(data)

        if (!this.localCurrentUrl && this.previewUrl) {
          this.localCurrentUrl = this.previewUrl
        }

        this.suppressCurrent = false

        this.$emit('updated', { data })
        this.$toast?.add({
          severity: 'success',
          detail: this.$t("Data saved"),
          life: settings.toastLife,
        })

        this.reset(false)
      } catch (err) {
        this.showError(err)
      } finally {
        this.isUploading = false
      }
    },

    async uploadOne(id, file) {
      if (!file) return null

      const fd = new FormData()
      fd.append(this.idParam, id)
      fd.append("field", this.field)
      fd.append("file", file)

      Object.entries(this.extraData || {}).forEach(([k, v]) => {
        if (v !== null && v !== undefined) fd.append(k, v)
      })

      const { data } = await httpClient.post(this.endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })

      if (!data?.success) throw data
      return data
    },

    applyServerUrl(data) {
      if (!data) return

      const candidates = [
        data.url,
        data.currentUrl,
        data?.user?.avatarUrl,
        data?.user?.profile_img_url,
        data?.branch?.document_logo_url,
        data?.branch?.site_logo_url,
        data?.branch?.[this.field + "_url"],
      ].filter(Boolean)

      if (candidates.length) {
        this.localCurrentUrl = candidates[0]
        this.currentUrlOk = true
        this.currentImgLoaded = false
        this.suppressCurrent = false
      }
    },

    async deleteOne() {
      if (this.disabled || this.isDeleting || this.multiple) return false

      const id = this.item?.[this.idParam] || this.item?.id

      if (this.deleteEndpoint && !id) return

      const prevLocal = this.localCurrentUrl
      const prevPreview = this.previewUrl
      const prevOk = this.currentUrlOk
      const prevSuppress = this.suppressCurrent
      const prevLoaded = this.currentImgLoaded

      this.suppressCurrent = true
      this.localCurrentUrl = null
      this.currentUrlOk = true
      this.currentImgLoaded = false
      this.reset(false)

      if (!this.deleteEndpoint) {
        this.$emit('deleted')
        return
      }

      this.isDeleting = true

      try {
        const payload = { [this.idParam]: id, field: this.field, ...this.extraData }
        const { data, status } = await httpClient.post(this.deleteEndpoint, payload)

        if (status >= 200 && status < 300 && (data?.success ?? true)) {
          this.suppressCurrent = true
          this.localCurrentUrl = null
          this.currentUrlOk = true
          this.currentImgLoaded = false
          this.reset(false)

          this.$emit('deleted')
          this.$emit('updated', { data })
          this.$toast?.add({
            severity: 'success',
            detail: this.$t('Data deleted'),
            life: settings.toastLife,
          })
        } else {
          throw data || new Error("Delete failed")
        }
      } catch (err) {
        this.suppressCurrent = prevSuppress
        this.localCurrentUrl = prevLocal
        this.currentUrlOk = prevOk
        this.currentImgLoaded = prevLoaded
        this.previewUrl = prevPreview
        this.showError(err)
      } finally {
        this.isDeleting = false
      }
    },

    reset(keepFile = false) {
      if (this.previewUrl && this.previewUrl !== this.localCurrentUrl) {
        URL.revokeObjectURL(this.previewUrl)
      }
      this.previewUrl = null
      if (!keepFile) this.file = null

      this.previews.forEach((p) => p.url && URL.revokeObjectURL(p.url))
      this.previews = []
      if (!keepFile) this.files = []
      this.fileKeys.clear()
    },
  },
}
</script>

<style scoped lang="scss">
.logo-card{
  border:1px solid #ebeef3;
  border-radius:3px;
  padding:12px;
  box-shadow:0 2px 8px rgba(0,0,0,0.04);
}
.logo-card__title{
  font-weight:500;
  margin-bottom:8px;
}

.logo-card__preview{
  border:1px dashed var(--border-color);
  border-radius:3px;
  display:inline-flex;        /* was flex */
  align-items:center;
  justify-content:center;
  margin-bottom:8px;
  overflow:hidden;
  cursor:pointer;
  position:relative;
  transition:.12s ease;
}

.logo-card__preview:hover {
  opacity: 0.9;
  //border-color:#3b82f6;
  border-color: var(--primary-color);
  //background:#eff6ff;
}

.logo-card__preview.is-drag-over{
  //border-color:#3b82f6;
  //background:#eff6ff;
  opacity: 0.9;
  border-color: var(--primary-color);
}

.logo-img {
  display:block;
  pointer-events:none;
}
.logo-placeholder {
  text-align: center;
  line-height: 1.3;
  font-size: 13px;
}
.drag-hint {
  position:absolute;
  inset:0;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:13px;
  font-weight:600;
  //background:rgba(239,246,255,0.7);
  pointer-events:none;
}

.single-delete-btn{
  position:absolute;
  right:6px;
  bottom:6px;
  z-index:5;
}

.logo-card__meta{
  min-height:18px;
  margin-bottom:8px;
  display:flex;
  gap:8px;
  align-items:center;
  flex-wrap:wrap;
}
.file-name{
  font-size:12px;
  word-break:break-all;
}

.file-size-hint{
  font-size:11px;
  color:#999;
}

.logo-card__actions{
  display:flex;
  flex-wrap: wrap;
  gap:6px;
  align-items:center;
}
.hidden-input{ display:none; }

/* multi preview */
.multi-preview-grid{
  display:flex;
  gap:6px;
  padding:6px;
  flex-wrap:wrap;
  justify-content:center;
}
.multi-preview-item{
  border:1px solid var(--border-color);
  border-radius:4px;
  overflow:hidden;
  position:relative;
  background:#fff;
}
.multi-remove{
  position:absolute;
  top:2px;
  right:2px;
  z-index:3;
}
.multi-preview-img{
  width:100%;
  height:100%;
  object-fit:cover;
  pointer-events:none;
}

/* placeholder for multiple */
.multi-preview-placeholder{
  width:100%;
  height:100%;
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  padding:4px;
  box-sizing:border-box;
}
.multi-preview-icon{
  font-size:20px;
  margin-bottom:4px;
}
.multi-preview-filename{
  font-size:10px;
  text-align:center;
  word-break:break-all;
}

/* cropper */
.cropper-modal{
  display:flex;
  flex-direction:column;
  gap:12px;
}
.cropper-viewport{
  position:relative;
  margin:0 auto;
  background:#111;
  overflow:hidden;
  border-radius:8px;
  touch-action:none;
}
.cropper-img{
  position:absolute;
  top:0; left:0;
  transform-origin: top left;
  user-select:none;
  -webkit-user-drag:none;
}
.cropper-mask{
  position:absolute;
  inset:0;
  box-shadow: 0 0 0 9999px rgba(0,0,0,0.35) inset;
  border:2px solid rgba(255,255,255,0.9);
  pointer-events:none;
}
.cropper-controls{
  display:flex;
  align-items:center;
  justify-content:center;
}
.zoom-row{
  display:flex;
  gap:10px;
  align-items:center;
  width:100%;
}
.zoom-label{
  font-size:12px;
  color:#555;
  min-width:45px;
}
.zoom-row input[type="range"]{
  width:100%;
}
</style>
